import { useEffect, useState } from "react";
import { ArrowRight, Check, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { DemoDialog } from "@/components/DemoDialog";
import { ChallengeIllustration, CooperativesIllustration, FinanceFlowIllustration, GovernanceIllustration, MarketplaceIllustration, TraceabilityIllustration } from "@/components/ValchainIllustrations";
import PlatformComparisonTable from "@/components/PlatformComparisonTable";
import {
  languages,
} from "@/data/agriHubContent";

const HERO_IMAGE_URL = "/assets/valchain/hero-agrihub.png";

const SectionLabel = ({ children, testId }) => <span data-testid={testId} className="section-label">{children}</span>;

const Reveal = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`reveal ${className}`}>{children}</div>
);

export default function ValchainLanding() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [language, setLanguage] = useState("en");
  const copy = languages[language];

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible"));
    }, { threshold: 0.16 });
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToPlatform = () => document.getElementById("platform")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid="valchain-landing-page" className="site-shell">
      <Toaster position="top-right" />
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} language={language} copy={copy} />
      <nav data-testid="site-navigation" className="nav-wrap">
        <a data-testid="brand-home-link" href="#top" className="brand-mark"><span>Valchain</span><em>AgriHub</em></a>
        <div data-testid="navigation-links" className="nav-links">
          <a data-testid="nav-ecosystem-link" href="#ecosystem">{copy.nav.ecosystem}</a>
          <a data-testid="nav-finance-link" href="#finance">{copy.nav.finance}</a>
          <a data-testid="nav-governance-link" href="#governance">{copy.nav.governance}</a>
        </div>
        <div data-testid="language-switcher" className="language-switcher">
          {Object.entries(languages).map(([code, item]) => (
            <button data-testid={`language-button-${code}`} className={language === code ? "active" : ""} key={code} onClick={() => setLanguage(code)} type="button">{item.name}</button>
          ))}
        </div>
        <Button data-testid="nav-demo-button" onClick={() => setDemoOpen(true)} className="nav-button">{copy.nav.demo}</Button>
      </nav>

      <section id="top" data-testid="hero-section" className="hero-section">
        <Reveal testId="hero-content" className="hero-copy">
          <SectionLabel testId="hero-eyebrow">{copy.heroEyebrow}</SectionLabel>
          <h1 data-testid="hero-headline">{copy.heroTitle}</h1>
          <p data-testid="hero-subheadline">{copy.heroSubtitle}</p>
          <div data-testid="hero-cta-group" className="cta-row">
            <Button data-testid="hero-request-demo-button" onClick={() => setDemoOpen(true)} className="primary-action">{copy.nav.demo} <ArrowRight /></Button>
            <Button data-testid="hero-explore-platform-button" onClick={scrollToPlatform} className="secondary-action" variant="outline">{copy.explore} <ChevronDown /></Button>
          </div>
          <div data-testid="core-story-strip" className="story-strip">
            {copy.storySteps.map((item, index) => <span data-testid={`core-story-step-${index}`} key={item}>{item}</span>)}
          </div>
        </Reveal>
        <Reveal testId="hero-visual" className="hero-visual hero-image-frame">
          <img data-testid="uploaded-hero-image" src={HERO_IMAGE_URL} alt="Valchain AgriHub platform interface shown as a premium agriculture value-chain operating system" />
        </Reveal>
      </section>

      <section data-testid="challenge-section" className="challenge-section section-pad">
        <Reveal testId="challenge-heading" className="section-heading narrow">
          <SectionLabel testId="challenge-eyebrow">{copy.sections.challenge.eyebrow}</SectionLabel>
          <h2 data-testid="challenge-title">{copy.sections.challenge.title}</h2>
        </Reveal>
        <div data-testid="challenge-grid" className="challenge-grid">
          <Reveal testId="challenge-visual"><ChallengeIllustration /></Reveal>
          <Reveal testId="challenge-list" className="challenge-cloud">
            {copy.challenges.map((item, index) => <span data-testid={`challenge-item-${index}`} key={item}>{item}</span>)}
          </Reveal>
        </div>
      </section>

      <section id="ecosystem" data-testid="ecosystem-section" className="ecosystem-section section-pad">
        <Reveal testId="ecosystem-heading" className="section-heading centered">
          <SectionLabel testId="ecosystem-eyebrow">{copy.sections.ecosystem.eyebrow}</SectionLabel>
          <h2 data-testid="ecosystem-title">{copy.sections.ecosystem.title}</h2>
        </Reveal>
        <Reveal testId="stakeholder-orbit" className="stakeholder-orbit">
          <div data-testid="agrihub-center-node" className="orbit-center"><Sparkles />Valchain AgriHub</div>
          {copy.stakeholders.map((name, index) => <span data-testid={`stakeholder-node-${index}`} className={`orbit-node node-pos-${index}`} key={name}>{name}</span>)}
        </Reveal>
      </section>

      <section id="platform" data-testid="journey-section" className="journey-section section-pad">
        <Reveal testId="journey-heading" className="section-heading split-heading">
          <SectionLabel testId="journey-eyebrow">{copy.sections.journey.eyebrow}</SectionLabel>
          <h2 data-testid="journey-title">{copy.sections.journey.title}</h2>
          <p data-testid="journey-copy">{copy.sections.journey.copy}</p>
        </Reveal>
        <div data-testid="journey-panels" className="journey-panels">
          {Object.entries(copy.journey).map(([title, items], index) => <Reveal testId={`journey-panel-${index}`} className="journey-panel" key={title}>
            <span data-testid={`journey-panel-index-${index}`} className="panel-index">0{index + 1}</span>
            <h3 data-testid={`journey-panel-title-${index}`}>{title}</h3>
            <div data-testid={`journey-panel-items-${index}`} className="tag-field">{items.map((item, itemIndex) => <span data-testid={`journey-panel-${index}-item-${itemIndex}`} key={item}>{item}</span>)}</div>
          </Reveal>)}
        </div>
        <PlatformComparisonTable />
      </section>

      <section data-testid="commerce-section" className="commerce-section section-pad editorial-grid">
        <Reveal testId="commerce-copy" className="editorial-copy">
          <SectionLabel testId="commerce-eyebrow">{copy.sections.commerce.eyebrow}</SectionLabel>
          <h2 data-testid="commerce-title">{copy.sections.commerce.title}</h2>
          <p data-testid="commerce-description">{copy.sections.commerce.copy}</p>
        </Reveal>
        <Reveal testId="commerce-illustration" className="section-visual"><MarketplaceIllustration /></Reveal>
        <Reveal testId="commerce-flow" className="commerce-flow">
          {copy.commerceCapabilities.map((item, index) => <div data-testid={`commerce-capability-${index}`} className="commerce-step" key={item}><Check />{item}</div>)}
        </Reveal>
      </section>

      <section id="finance" data-testid="finance-section" className="finance-section section-pad">
        <Reveal testId="finance-heading" className="section-heading centered">
          <SectionLabel testId="finance-eyebrow">{copy.sections.finance.eyebrow}</SectionLabel>
          <h2 data-testid="finance-title">{copy.sections.finance.title}</h2>
          <p data-testid="finance-description">{copy.sections.finance.copy}</p>
        </Reveal>
        <Reveal testId="finance-flow"><FinanceFlowIllustration /></Reveal>
        <Reveal testId="finance-benefits" className="benefit-rail">{copy.financeBenefits.map((item, index) => <span data-testid={`finance-benefit-${index}`} key={item}>{item}</span>)}</Reveal>
      </section>

      <section data-testid="disbursement-section" className="disbursement-section section-pad">
        <Reveal testId="disbursement-heading" className="section-heading narrow">
          <SectionLabel testId="disbursement-eyebrow">{copy.sections.disbursement.eyebrow}</SectionLabel>
          <h2 data-testid="disbursement-title">{copy.sections.disbursement.title}</h2>
          <p data-testid="disbursement-description">{copy.sections.disbursement.copy}</p>
        </Reveal>
        <div data-testid="disbursement-timeline" className="milestone-timeline">
          {copy.disbursementMilestones.map(([event, release], index) => <Reveal testId={`milestone-${index}`} className="milestone" key={event}>
            <span data-testid={`milestone-index-${index}`} className="milestone-dot">✓</span>
            <h3 data-testid={`milestone-event-${index}`}>{event}</h3>
            <p data-testid={`milestone-release-${index}`}>{release}</p>
          </Reveal>)}
        </div>
      </section>

      <section data-testid="traceability-section" className="traceability-section section-pad">
        <Reveal testId="traceability-heading" className="section-heading centered">
          <SectionLabel testId="traceability-eyebrow">{copy.sections.traceability.eyebrow}</SectionLabel>
          <h2 data-testid="traceability-title">{copy.sections.traceability.title}</h2>
        </Reveal>
        <Reveal testId="traceability-visual" className="section-visual compact-visual"><TraceabilityIllustration /></Reveal>
        <Reveal testId="traceability-chain" className="trace-chain">{copy.traceabilitySteps.map((step, index) => <span data-testid={`traceability-step-${index}`} key={step}>{step}</span>)}</Reveal>
      </section>

      <section id="governance" data-testid="governance-section" className="governance-section section-pad editorial-grid reverse-grid">
        <Reveal testId="governance-visual" className="governance-visual-card"><GovernanceIllustration /></Reveal>
        <Reveal testId="governance-copy" className="editorial-copy">
          <SectionLabel testId="governance-eyebrow">{copy.sections.governance.eyebrow}</SectionLabel>
          <h2 data-testid="governance-title">{copy.sections.governance.title}</h2>
          <div data-testid="governance-capabilities" className="tag-field compact">{copy.governanceCapabilities.map((item, index) => <span data-testid={`governance-capability-${index}`} key={item}>{item}</span>)}</div>
        </Reveal>
      </section>

      <section data-testid="cooperatives-section" className="cooperatives-section section-pad">
        <Reveal testId="cooperatives-heading" className="section-heading narrow">
          <SectionLabel testId="cooperatives-eyebrow">{copy.sections.cooperatives.eyebrow}</SectionLabel>
          <h2 data-testid="cooperatives-title">{copy.sections.cooperatives.title}</h2>
        </Reveal>
        <Reveal testId="cooperatives-visual" className="section-visual compact-visual"><CooperativesIllustration /></Reveal>
        <Reveal testId="cooperative-outcomes" className="cooperative-board">{copy.cooperativeOutcomes.map((item, index) => <article data-testid={`cooperative-outcome-${index}`} key={item}><span>{index + 1}</span><p>{item}</p></article>)}</Reveal>
      </section>

      <section data-testid="impact-section" className="impact-section section-pad">
        <Reveal testId="impact-heading" className="section-heading centered">
          <SectionLabel testId="impact-eyebrow">{copy.sections.impact.eyebrow}</SectionLabel>
          <h2 data-testid="impact-title">{copy.sections.impact.title}</h2>
        </Reveal>
        <Reveal testId="impact-metrics" className="impact-grid">{copy.impactMetrics.map(([metric, metricCopy], index) => <article data-testid={`impact-metric-${index}`} key={metric}><strong data-testid={`impact-metric-title-${index}`}>{metric}</strong><p data-testid={`impact-metric-copy-${index}`}>{metricCopy}</p></article>)}</Reveal>
      </section>

      <section data-testid="final-cta-section" className="final-cta section-pad">
        <Reveal testId="final-cta-content" className="final-cta-card">
          <SectionLabel testId="final-cta-eyebrow">{copy.sections.finalCta.eyebrow}</SectionLabel>
          <h2 data-testid="final-cta-title">{copy.sections.finalCta.title}</h2>
          <p data-testid="final-cta-subtitle">{copy.sections.finalCta.copy}</p>
          <Button data-testid="final-request-demo-button" onClick={() => setDemoOpen(true)} className="primary-action">{copy.nav.demo} <ArrowRight /></Button>
        </Reveal>
      </section>

      <footer data-testid="site-footer" className="site-footer">
        <div data-testid="footer-brand-block" className="footer-brand-block">
          <a data-testid="footer-brand-link" href="#top" className="brand-mark footer-brand"><span>Valchain</span><em>AgriHub</em></a>
          <p data-testid="footer-positioning">Digital Infrastructure for Agricultural Value Chains</p>
        </div>
        <div data-testid="footer-contact-details" className="footer-contact-details">
          <div data-testid="footer-company-card" className="footer-card">
            <span data-testid="footer-company-label">Company</span>
            <p data-testid="footer-company-name">ValChain Solutions Pvt. Ltd.</p>
          </div>
          <div data-testid="footer-contact-card" className="footer-card">
            <span data-testid="footer-contact-label">Contact</span>
            <a data-testid="footer-email-link" href="mailto:info@valchain.in">info@valchain.in</a>
          </div>
          <div data-testid="footer-demo-card" className="footer-card">
            <span data-testid="footer-demo-label">Demo Requests</span>
            <button data-testid="footer-demo-button" type="button" onClick={() => setDemoOpen(true)}>Request a Demo</button>
          </div>
        </div>
        <div data-testid="footer-copyright" className="footer-bottom">
          © Valchain AgriHub. All rights reserved.
        </div>
      </footer>
    </main>
  );
}